import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { ASSESSMENT_IDENTITY_KEY, readAssessmentStorage } from "./assessmentStorage";

const POLL_INTERVAL_MS = 2000;

const cardStyle = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 16,
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #d1d5db",
  fontSize: 14,
  boxSizing: "border-box",
};

const buttonStyle = {
  padding: "10px 16px",
  borderRadius: 8,
  border: "none",
  fontWeight: 700,
};

const primaryButtonStyle = {
  ...buttonStyle,
  background: "#3F51B5",
  color: "#ffffff",
  cursor: "pointer",
};

const disabledButtonStyle = {
  ...buttonStyle,
  background: "#d1d5db",
  color: "#6b7280",
  cursor: "not-allowed",
};

function getPayload(raw = {}) {
  if (raw?.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    return { ...raw, ...raw.data };
  }
  return raw || {};
}

function getErrorPayload(error) {
  return getPayload(error?.response?.data || {});
}

export function getAssessmentErrorMessage(error, fallback) {
  const data = getErrorPayload(error);
  const code = data?.error_code || data?.error || data?.code || "";
  const safeMessages = {
    submission_not_found: "Assessment submission not found.",
    submission_not_evaluated: "This assessment has not finished evaluation yet.",
    evaluation_result_not_found: "The assessment evaluation is not available yet.",
    evaluation_result_malformed: "The assessment evaluation could not be used.",
    report_storage_failed: "The report could not be stored. Please try again.",
    report_reference_persistence_failed:
      "The report could not be saved. Please try again.",
    submission_finalization_failed:
      "The submission could not be finalised. Please try again.",
    usage_finalization_failed:
      "The report is ready, but usage finalisation must be retried.",
    report_access_unavailable:
      "The report is temporarily unavailable. Please try again.",
    email_not_verified: "Please verify your email address again.",
  };

  return data?.message || safeMessages[code] || fallback;
}

export function getAssessmentStatusLabel(state = {}) {
  if (state.usage_status === "failed") return "Finalisation needs retry";
  if (state.usage_status === "finalizing") return "Finalising usage";
  if (state.usage_status === "finalized" && state.report_available) {
    return "Report ready";
  }
  if (state.report_available) return "Report stored";
  if (state.status === "generating" || state.stage === "report_generating") {
    return "Generating report";
  }
  if (state.evaluation_available) return "Evaluation complete";
  return "Waiting for evaluation";
}

export default function PublicAssessmentReportPage({ submissionId: submissionIdProp }) {
  const { submissionId: routeSubmissionId } = useParams();
  const resolvedSubmissionId = submissionIdProp || routeSubmissionId || "";

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [verified, setVerified] = useState(false);
  const [userId, setUserId] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [identityStatus, setIdentityStatus] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);

  const [submissionState, setSubmissionState] = useState(null);
  const [pageStatus, setPageStatus] = useState("");
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [reportRequestRunning, setReportRequestRunning] = useState(false);
  const [reportUrl, setReportUrl] = useState("");

  const pollTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const reportRequestRef = useRef(false);
  const identityRef = useRef({ email: "", user_id: "" });

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    const storedIdentity = readAssessmentStorage(ASSESSMENT_IDENTITY_KEY);
    if (storedIdentity?.email && storedIdentity?.user_id) {
      setEmail(storedIdentity.email);
      setIdentityStatus("Restoring verified email…");
      axios.get("/api/public/oral/email-verified", { params: storedIdentity })
        .then(async (response) => {
          if (!mountedRef.current) return;
          const payload = getPayload(response.data);
          if (!payload.verified) {
            localStorage.removeItem(ASSESSMENT_IDENTITY_KEY);
            setIdentityStatus("Your verification has expired. Please verify your email again.");
            return;
          }
          identityRef.current = storedIdentity;
          setUserId(storedIdentity.user_id);
          setUserEmail(storedIdentity.email);
          setVerified(true);
          setIdentityStatus("Email verified.");
          await loadSubmissionStatus(storedIdentity.email, storedIdentity.user_id);
        })
        .catch(() => {
          if (!mountedRef.current) return;
          localStorage.removeItem(ASSESSMENT_IDENTITY_KEY);
          setIdentityStatus("Could not restore verification. Please verify your email again.");
        });
    }
    return () => {
      mountedRef.current = false;
      // Polling must not outlive this submission page.
      stopPolling();
    };
    // Identity restoration is a mount-only bootstrap; report state changes must not restart it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const identityParams = () => identityRef.current;

  const reportEndpoint = `/api/assessments/submissions/${encodeURIComponent(
    resolvedSubmissionId
  )}/report`;

  const applyReportState = (payload) => {
    setSubmissionState((previous) => ({ ...(previous || {}), ...payload }));
    if (payload?.report_url) {
      setReportUrl(payload.report_url);
    }

    // Report persistence and usage finalisation are separate backend states.
    if (payload?.usage_status === "finalized") {
      stopPolling();
      setPageStatus("Report ready.");
    } else if (payload?.usage_status === "failed") {
      stopPolling();
      setPageStatus(
        payload?.message ||
          "The report is ready, but usage finalisation must be retried."
      );
    } else if (payload?.usage_status === "finalizing") {
      setPageStatus("Finalising report usage…");
    } else if (payload?.report_available) {
      setPageStatus("Report stored. Finalisation has not completed.");
    } else if (payload?.evaluation_available) {
      setPageStatus("Evaluation complete. Generate the report when ready.");
    } else {
      setPageStatus(payload?.message || "Waiting for evaluation.");
    }
  };

  const fetchReportState = async () => {
    try {
      const response = await axios.get(reportEndpoint, {
        params: identityParams(),
      });
      if (!mountedRef.current) return null;
      const payload = getPayload(response.data);
      applyReportState(payload);
      return payload;
    } catch (error) {
      if (!mountedRef.current) return null;
      stopPolling();
      setPageStatus(
        getAssessmentErrorMessage(
          error,
          "The report is temporarily unavailable. Please try again."
        )
      );
      return null;
    }
  };

  const startPolling = () => {
    if (pollTimerRef.current) return;
    pollTimerRef.current = setInterval(() => {
      fetchReportState();
    }, POLL_INTERVAL_MS);
  };

  const loadSubmissionStatus = async (verifiedEmail, verifiedUserId) => {
    setLoadingStatus(true);
    setPageStatus("Loading assessment status…");
    setReportUrl("");
    try {
      const response = await axios.get(
        `/api/assessments/submissions/${encodeURIComponent(
          resolvedSubmissionId
        )}`,
        {
          params: {
            email: verifiedEmail,
            user_id: verifiedUserId,
          },
        }
      );
      const payload = getPayload(response.data);
      if (!mountedRef.current) return;
      applyReportState(payload);

      if (payload.usage_status === "finalizing") {
        startPolling();
      } else if (payload.report_available) {
        await fetchReportState();
      }
    } catch (error) {
      if (!mountedRef.current) return;
      setSubmissionState(null);
      setPageStatus(
        getAssessmentErrorMessage(error, "Could not load this assessment.")
      );
    } finally {
      if (mountedRef.current) setLoadingStatus(false);
    }
  };

  const sendEmailCode = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setIdentityStatus("Please enter your email address.");
      return;
    }
    setSendingCode(true);
    setIdentityStatus("Sending verification code…");
    try {
      await axios.post("/api/public/oral/send-email-code", {
        email: normalizedEmail,
      });
      setEmail(normalizedEmail);
      setEmailCodeSent(true);
      setIdentityStatus("Verification code sent.");
    } catch (error) {
      setIdentityStatus(
        getAssessmentErrorMessage(
          error,
          "Could not send the verification code. Please try again."
        )
      );
    } finally {
      setSendingCode(false);
    }
  };

  const verifyEmailCode = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !code.trim()) {
      setIdentityStatus("Enter your email and verification code.");
      return;
    }
    setVerifyingCode(true);
    setIdentityStatus("Verifying email…");
    try {
      const response = await axios.post("/api/public/oral/verify-email-code", {
        email: normalizedEmail,
        code: code.trim(),
        marketing_email_consent: false,
      });
      const payload = getPayload(response.data);
      const verifiedUserId = payload.user_id || "";
      const verifiedEmail = (payload.user_email || normalizedEmail)
        .trim()
        .toLowerCase();
      if (!verifiedUserId) {
        setIdentityStatus("Email verified, but no user ID was returned.");
        return;
      }
      setUserId(verifiedUserId);
      setUserEmail(verifiedEmail);
      identityRef.current = {
        email: verifiedEmail,
        user_id: verifiedUserId,
      };
      localStorage.setItem(ASSESSMENT_IDENTITY_KEY, JSON.stringify(identityRef.current));
      setVerified(true);
      setIdentityStatus("Email verified.");
      await loadSubmissionStatus(verifiedEmail, verifiedUserId);
    } catch (error) {
      setVerified(false);
      setIdentityStatus(
        getAssessmentErrorMessage(
          error,
          "Email verification failed. Please request a new code."
        )
      );
    } finally {
      setVerifyingCode(false);
    }
  };

  const requestReport = async () => {
    if (reportRequestRef.current || !verified || !userId || !userEmail) return;
    reportRequestRef.current = true;
    setReportRequestRunning(true);
    setPageStatus(
      submissionState?.usage_status === "failed"
        ? "Retrying report finalisation…"
        : "Generating report…"
    );
    try {
      const response = await axios.post(reportEndpoint, identityParams());
      const payload = getPayload(response.data);
      applyReportState(payload);
      if (payload.usage_status === "finalizing") {
        startPolling();
      } else if (payload.usage_status === "finalized") {
        await fetchReportState();
      }
    } catch (error) {
      const data = getErrorPayload(error);
      if (
        error?.response?.status === 503 &&
        (data?.error === "usage_finalization_failed" ||
          data?.error_code === "usage_finalization_failed")
      ) {
        // The same idempotent POST safely retries finalisation of the stored report.
        applyReportState({
          ...data,
          report_available:
            data?.report_available ?? submissionState?.report_available ?? true,
          usage_status: "failed",
        });
      } else {
        setPageStatus(
          getAssessmentErrorMessage(
            error,
            "The report request could not be completed. Please try again."
          )
        );
      }
    } finally {
      reportRequestRef.current = false;
      setReportRequestRunning(false);
    }
  };

  const canGenerate =
    verified &&
    submissionState?.evaluation_available === true &&
    submissionState?.usage_status !== "finalized" &&
    submissionState?.usage_status !== "finalizing";
  const retryNeeded = submissionState?.usage_status === "failed";

  return (
    <main
      style={{
        maxWidth: 760,
        margin: "0 auto",
        padding: 16,
        color: "#111827",
      }}
    >
      <h1>Assessment Report</h1>
      <p style={{ color: "#4b5563" }}>
        Verify your email to check and retrieve this assessment report.
      </p>

      <section style={{ ...cardStyle, marginBottom: 16 }}>
        <h2 style={{ marginTop: 0 }}>Verify identity</h2>
        <label htmlFor="assessment-report-email" style={{ fontWeight: 700 }}>
          Email
        </label>
        <input
          id="assessment-report-email"
          type="email"
          style={{ ...inputStyle, marginTop: 6 }}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={verified || sendingCode || verifyingCode}
        />
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            style={sendingCode || verified ? disabledButtonStyle : primaryButtonStyle}
            disabled={sendingCode || verified}
            onClick={sendEmailCode}
          >
            {sendingCode ? "Sending…" : "Send verification code"}
          </button>
        </div>
        {emailCodeSent && !verified ? (
          <div style={{ marginTop: 12 }}>
            <label htmlFor="assessment-report-code" style={{ fontWeight: 700 }}>
              Verification code
            </label>
            <input
              id="assessment-report-code"
              style={{ ...inputStyle, marginTop: 6 }}
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
            <button
              type="button"
              style={{
                ...(verifyingCode ? disabledButtonStyle : primaryButtonStyle),
                marginTop: 10,
              }}
              disabled={verifyingCode}
              onClick={verifyEmailCode}
            >
              {verifyingCode ? "Verifying…" : "Verify email"}
            </button>
          </div>
        ) : null}
        <div aria-live="polite" style={{ marginTop: 10 }}>
          {identityStatus}
        </div>
      </section>

      <section style={{ ...cardStyle, marginBottom: 16 }}>
        <h2 style={{ marginTop: 0 }}>Submission status</h2>
        <div><b>Submission:</b> {resolvedSubmissionId || "-"}</div>
        <div style={{ marginTop: 8 }}>
          <b>Status:</b>{" "}
          {submissionState
            ? getAssessmentStatusLabel(submissionState)
            : verified
            ? "Loading"
            : "Verify your email to continue"}
        </div>
        <div aria-live="polite" style={{ marginTop: 10 }}>
          {loadingStatus ? "Loading assessment status…" : pageStatus}
        </div>
      </section>

      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Report</h2>
        {canGenerate ? (
          <button
            type="button"
            style={
              reportRequestRunning ? disabledButtonStyle : primaryButtonStyle
            }
            disabled={reportRequestRunning}
            onClick={requestReport}
          >
            {reportRequestRunning
              ? retryNeeded
                ? "Retrying…"
                : "Generating…"
              : retryNeeded
              ? "Retry Finalisation"
              : "Generate Report"}
          </button>
        ) : null}
        {reportUrl ? (
          <button
            type="button"
            style={{ ...primaryButtonStyle, marginLeft: canGenerate ? 10 : 0 }}
            onClick={() =>
              window.open(reportUrl, "_blank", "noopener,noreferrer")
            }
          >
            View Report
          </button>
        ) : null}
      </section>
    </main>
  );
}
